import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type SessionUser = {
  id: string;
  name: string;
  email: string;
  role: "fan" | "promoter" | "admin";
  promoterId?: string | null;
  image?: string | null;
};

type BrowserSession = {
  accessToken: string;
  expiresAtUtc: string;
  user: SessionUser;
};

type AuthContextValue = {
  user: SessionUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<SessionUser>;
  acceptSession(session: BrowserSession): void;
  logout(): void;
};

const storageKey = "edaga.browser-session";
let memoryToken: string | null = null;

function readSession(): BrowserSession | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const session = JSON.parse(raw) as BrowserSession;
    if (new Date(session.expiresAtUtc).getTime() <= Date.now()) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    memoryToken = session.accessToken;
    return session;
  } catch {
    sessionStorage.removeItem(storageKey);
    return null;
  }
}

export function getAccessToken() {
  if (memoryToken) return memoryToken;
  return readSession()?.accessToken ?? null;
}

function persistSession(session: BrowserSession) {
  memoryToken = session.accessToken;
  sessionStorage.setItem(storageKey, JSON.stringify(session));
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children, deferStoredSession = false }: { children: ReactNode; deferStoredSession?: boolean }) {
  const initial = useMemo(() => deferStoredSession ? null : readSession(), [deferStoredSession]);
  const [user, setUser] = useState<SessionUser | null>(initial?.user ?? null);
  // A non-expired browser session is sufficient to render immediately. The
  // server validation below refreshes it in the background; protected API
  // requests still enforce the token on every call.
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const session = initial ?? (deferStoredSession ? readSession() : null);
    if (!session) return;
    if (deferStoredSession) setUser(session.user);
    fetch(`${import.meta.env.VITE_API_URL ?? "/api/spa"}/auth/session`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Session expired");
        const refreshed = (await response.json()) as BrowserSession;
        persistSession(refreshed);
        setUser(refreshed.user);
      })
      .catch(() => {
        memoryToken = null;
        sessionStorage.removeItem(storageKey);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [deferStoredSession, initial]);

  async function login(email: string, password: string) {
    const response = await fetch(`${import.meta.env.VITE_API_URL ?? "/api/spa"}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = (await response.json()) as BrowserSession & { error?: string };
    if (!response.ok) throw new Error(data.error ?? "Sign in failed");
    persistSession(data);
    setUser(data.user);
    return data.user;
  }

  function acceptSession(session: BrowserSession) {
    persistSession(session);
    setUser(session.user);
  }

  function logout() {
    memoryToken = null;
    sessionStorage.removeItem(storageKey);
    setUser(null);
  }

  return <AuthContext.Provider value={{ user, loading, login, acceptSession, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
